import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiCamera,
  FiRefreshCw,
  FiCheck,
  FiUploadCloud,
  FiAlertCircle,
  FiSun,
  FiUser,
  FiShield,
  FiArrowLeft,
  FiRotateCcw,
  FiCheckCircle,
  FiInfo,
  FiEye,
  FiLock,
  FiUnlock,
} from "react-icons/fi";
import api from "../utils/axiosConfig.js";
import "../styles/pages/SelfieCapture.css";

const SelfieCapture = () => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const analysisIntervalRef = useRef(null);
  const streamRef = useRef(null);
  const faceDetectorRef = useRef(null);

  const [imageSrc, setImageSrc] = useState(null);
  const [cameraStatus, setCameraStatus] = useState("loading"); // "loading" | "ready" | "denied"
  const [facingMode] = useState("user"); // "user" | "environment"
  const [isCapturing, setIsCapturing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isFlashActive, setIsFlashActive] = useState(false);

  // Real-Time Face Alignment State
  const [alignmentState, setAlignmentState] = useState({
    isAligned: false,
    message: "Initializing face scanner…",
    status: "checking", // "checking" | "warn" | "good"
    faceCount: 0,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromProfile = searchParams.get("from") === "profile";

  // ---------- Load MediaPipe Face Detector (Tasks-Vision) ----------
  useEffect(() => {
    let isMounted = true;

    const initFaceDetector = async () => {
      try {
        const visionModule = await import(
          /* webpackIgnore: true */ "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/+esm"
        );
        const { FilesetResolver, FaceDetector } = visionModule;

        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm"
        );

        if (!isMounted) return;

        const detector = await FaceDetector.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite",
            delegate: "GPU",
          },
          runningMode: "VIDEO",
          minDetectionConfidence: 0.55,
        });

        if (isMounted) {
          faceDetectorRef.current = detector;
        }
      } catch (err) {
        console.warn("MediaPipe CDN load note:", err);
        if ("FaceDetector" in window) {
          try {
            // @ts-ignore
            faceDetectorRef.current = new window.FaceDetector({
              maxDetectedFaces: 3,
              fastMode: true,
            });
          } catch (e) { }
        }
      }
    };

    initFaceDetector();

    return () => {
      isMounted = false;
      if (faceDetectorRef.current?.close) {
        try {
          faceDetectorRef.current.close();
        } catch (e) { }
      }
    };
  }, []);

  // ---------- Camera Stream Lifecycle ----------
  const startWebcam = useCallback(async (facing = "user") => {
    setCameraStatus("loading");

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    try {
      const constraints = {
        video: {
          facingMode: facing,
          width: { ideal: 1080, min: 640 },
          height: { ideal: 1080, min: 640 },
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (webcamRef.current) {
        webcamRef.current.srcObject = stream;
        webcamRef.current.onloadedmetadata = () => {
          setCameraStatus("ready");
          webcamRef.current.play().catch(() => { });
        };
      }
    } catch (err) {
      console.warn("High-res camera init failed, attempting standard constraints:", err);
      try {
        const fallbackStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        streamRef.current = fallbackStream;
        if (webcamRef.current) {
          webcamRef.current.srcObject = fallbackStream;
          webcamRef.current.onloadedmetadata = () => {
            setCameraStatus("ready");
            webcamRef.current.play().catch(() => { });
          };
        }
      } catch (fallbackErr) {
        console.error("Camera access denied or unavailable:", fallbackErr);
        setCameraStatus("denied");
      }
    }
  }, []);

  useEffect(() => {
    startWebcam(facingMode);

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (analysisIntervalRef.current) {
        clearInterval(analysisIntervalRef.current);
      }
    };
  }, [facingMode, startWebcam]);

  // ---------- Real-Time Face Alignment & Quality Analysis ----------
  const analyzeFrame = useCallback(async () => {
    const video = webcamRef.current;
    if (!video || video.readyState < 2 || video.videoWidth === 0) return;

    const videoW = video.videoWidth;
    const videoH = video.videoHeight;

    // 1. Compute Lighting & Sharpness
    const w = 96;
    const h = 96;
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);

    let data;
    try {
      data = ctx.getImageData(0, 0, w, h).data;
    } catch {
      return;
    }

    let sum = 0;
    const gray = new Uint8ClampedArray(w * h);
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
      const l = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      gray[p] = l;
      sum += l;
    }
    const avgBrightness = sum / (w * h);

    let edgeSum = 0;
    let edgeSqSum = 0;
    let count = 0;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const idx = y * w + x;
        const lap =
          4 * gray[idx] - gray[idx - 1] - gray[idx + 1] - gray[idx - w] - gray[idx + w];
        edgeSum += lap;
        edgeSqSum += lap * lap;
        count++;
      }
    }
    const mean = edgeSum / count;
    const variance = edgeSqSum / count - mean * mean;

    // 2. Perform Real-Time Face Detection
    let detectedFaces = [];
    const detector = faceDetectorRef.current;

    if (detector) {
      try {
        if (typeof detector.detectForVideo === "function") {
          const result = detector.detectForVideo(video, performance.now());
          if (result && result.detections) {
            detectedFaces = result.detections.map((d) => ({
              x: d.boundingBox.originX,
              y: d.boundingBox.originY,
              width: d.boundingBox.width,
              height: d.boundingBox.height,
            }));
          }
        } else if (typeof detector.detect === "function") {
          const result = await detector.detect(video);
          if (result && Array.isArray(result)) {
            detectedFaces = result.map((f) => ({
              x: f.boundingBox.x,
              y: f.boundingBox.y,
              width: f.boundingBox.width,
              height: f.boundingBox.height,
            }));
          }
        }
      } catch (e) { }
    }

    // 3. Evaluate Criteria against Oval Bounds
    if (detectedFaces.length === 0) {
      setAlignmentState({
        isAligned: false,
        message: "No face detected — place your face in the circle",
        status: "warn",
        faceCount: 0,
      });
      return;
    }

    if (detectedFaces.length > 1) {
      setAlignmentState({
        isAligned: false,
        message: "Multiple faces detected — only 1 person allowed",
        status: "warn",
        faceCount: detectedFaces.length,
      });
      return;
    }

    // Single Face Detected -> Check Oval Centering and Distance
    const face = detectedFaces[0];
    const faceCenterX = (face.x + face.width / 2) / videoW;
    const faceCenterY = (face.y + face.height / 2) / videoH;
    const faceSizeRatio = Math.max(face.width / videoW, face.height / videoH);

    const isCenteredX = Math.abs(faceCenterX - 0.5) <= 0.17;
    const isCenteredY = Math.abs(faceCenterY - 0.5) <= 0.19;

    if (!isCenteredX || !isCenteredY) {
      setAlignmentState({
        isAligned: false,
        message: "Position your face inside the center oval",
        status: "warn",
        faceCount: 1,
      });
      return;
    }

    // Face Distance Check
    if (faceSizeRatio < 0.26) {
      setAlignmentState({
        isAligned: false,
        message: "Move closer to the camera",
        status: "warn",
        faceCount: 1,
      });
      return;
    }

    if (faceSizeRatio > 0.74) {
      setAlignmentState({
        isAligned: false,
        message: "Step back slightly from camera",
        status: "warn",
        faceCount: 1,
      });
      return;
    }

    // Lighting & Sharpness Checks
    if (avgBrightness < 48) {
      setAlignmentState({
        isAligned: false,
        message: "Lighting too low — face a light or turn on Fill Light",
        status: "warn",
        faceCount: 1,
      });
      return;
    }

    if (avgBrightness > 220) {
      setAlignmentState({
        isAligned: false,
        message: "Too bright — reduce direct glare",
        status: "warn",
        faceCount: 1,
      });
      return;
    }

    if (variance < 105) {
      setAlignmentState({
        isAligned: false,
        message: "Camera is blurry — hold camera steady",
        status: "warn",
        faceCount: 1,
      });
      return;
    }

    // ALL CHECKS PASSED: Face is aligned!
    setAlignmentState({
      isAligned: true,
      message: "Face aligned perfectly — Click to capture!",
      status: "good",
      faceCount: 1,
    });
  }, []);

  useEffect(() => {
    if (cameraStatus === "ready" && !imageSrc) {
      analysisIntervalRef.current = setInterval(analyzeFrame, 220);
    }
    return () => {
      if (analysisIntervalRef.current) {
        clearInterval(analysisIntervalRef.current);
      }
    };
  }, [cameraStatus, imageSrc, analyzeFrame]);

  // ---------- Snapshot Execution ----------
  const captureSnapshot = () => {
    const video = webcamRef.current;
    if (!video) return;

    setIsFlashActive(true);
    setTimeout(() => setIsFlashActive(false), 250);

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 720;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");

    if (facingMode === "user") {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataURL = canvas.toDataURL("image/jpeg", 0.95);
    setImageSrc(dataURL);
    setIsCapturing(false);
  };

  // ---------- Capture Trigger (Strictly Enforced) ----------
  const handleCaptureClick = () => {
    if (!alignmentState.isAligned || isCapturing || cameraStatus !== "ready") {
      if (!alignmentState.isAligned) {
        toast.warning(alignmentState.message || "Please align your face properly inside the circle first.");
      }
      return;
    }

    captureSnapshot();
  };

  const handleRetake = () => {
    setImageSrc(null);
    setAlignmentState({
      isAligned: false,
      message: "Align face inside circle…",
      status: "checking",
      faceCount: 0,
    });
  };

  // ---------- File Upload Fallback ----------
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file (JPG, PNG).");
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      toast.error("File is too large. Please choose an image under 15MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setImageSrc(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // ---------- Final Upload and Submission ----------
  const continueToNext = async () => {
    if (!imageSrc) return;

    setIsUploading(true);
    try {
      const blob = await (await fetch(imageSrc)).blob();
      const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });

      const formData = new FormData();
      formData.append("selfie", file);

      const response = await api.post("/auth/upload-selfie", formData);

      toast.success("Selfie verified & saved successfully!");
      const existingUser = JSON.parse(localStorage.getItem("user") || "{}");
      const updatedUser = { ...existingUser, ...(response.data.user || {}) };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      const pendingCode = sessionStorage.getItem("pendingJoinCode");
      if (pendingCode) {
        sessionStorage.removeItem("pendingJoinCode");
        try {
          const joinRes = await api.post("/groups/join", { code: pendingCode });
          if (joinRes.data?.groupId) {
            const dashboardPath = updatedUser.role === "admin" ? "/admin-dashboard" : "/user-dashboard";
            navigate(dashboardPath, { replace: true });
            setTimeout(() => {
              navigate(`/group/${joinRes.data.groupId}`);
            }, 10);
            return;
          }
        } catch (joinErr) {
          console.error("Auto-join error:", joinErr);
        }
      }

      if (fromProfile) {
        navigate("/profile-setting", { replace: true });
        return;
      }

      if (updatedUser.role === "admin") {
        navigate("/admin-dashboard", { replace: true });
      } else {
        navigate("/user-dashboard", { replace: true });
      }
    } catch (error) {
      console.error("Upload error:", error);
      const message =
        error.response?.data?.message ||
        "Failed to upload selfie. Please ensure your face is clearly visible.";
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleBack = () => {
    if (fromProfile) {
      navigate("/profile-setting");
      return;
    }

    const user = localStorage.getItem("user");
    if (user) {
      try {
        const parsed = JSON.parse(user);
        navigate(parsed.role === "admin" ? "/admin-dashboard" : "/user-dashboard");
        return;
      } catch (e) { }
    }
    navigate("/login");
  };

  return (
    <div className="selfie-page">
      {/* Visual Shutter Flash Effect */}
      {isFlashActive && <div className="shutter-flash" />}

      {/* Top Professional Header */}
      <header className="selfie-header">
        <div className="selfie-header-content">
          <button className="header-back-btn" onClick={handleBack} title="Back to Dashboard">
            <FiArrowLeft className="back-icon" />
            <span className="back-text">Dashboard</span>
          </button>

          <div className="header-brand">
            <img
              src="/images/logo_colored_white.png"
              alt="Fotographiya"
              className="brand-logo"
            />
          </div>

          <div className="header-badge">
            <FiShield className="shield-icon" />
            <span>AI Face ID</span>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="selfie-main-container">
        <div className="selfie-card">
          {/* Compact Guidelines View (Moved to Top) */}
          <div className="selfie-guidelines-compact">
            <div className="guideline-compact-item">
              <FiSun className="icon-purple" />
              <span>Good Light</span>
            </div>
            <div className="guideline-compact-item">
              <FiUser className="icon-blue" />
              <span>Single Face</span>
            </div>
            <div className="guideline-compact-item">
              <FiEye className="icon-green" />
              <span>No Glasses</span>
            </div>
            <div className="guideline-compact-item">
              <FiCamera className="icon-amber" />
              <span>Look Directly</span>
            </div>
          </div>

          {/* Viewfinder Center Section */}
          <div className="selfie-viewfinder-wrapper">
            <div
              className={`camera-frame ${imageSrc ? "camera-frame--captured" : ""} ${alignmentState.isAligned && !imageSrc ? "camera-frame--aligned-glow" : ""}`}
            >
              {/* Camera Loading State */}
              {cameraStatus === "loading" && !imageSrc && (
                <div className="camera-state camera-state--loading">
                  <div className="camera-spinner" />
                  <p className="state-title">Starting High-Definition Camera…</p>
                  <p className="state-desc">Please grant camera permissions if prompted</p>
                </div>
              )}

              {/* Camera Access Denied State */}
              {cameraStatus === "denied" && !imageSrc && (
                <div className="camera-state camera-state--denied">
                  <div className="denied-icon-wrap">
                    <FiAlertCircle className="denied-icon" />
                  </div>
                  <h3 className="state-title">Camera Access Blocked</h3>
                  <p className="state-desc">
                    Your browser is blocking camera access. Please allow camera permissions in your site settings, or simply upload a selfie from your device.
                  </p>
                  <div className="denied-actions">
                    <button
                      className="btn-retry-camera"
                      onClick={() => startWebcam(facingMode)}
                    >
                      <FiRefreshCw /> Try Again
                    </button>
                    <button
                      className="btn-fallback-upload"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <FiUploadCloud /> Upload from Device
                    </button>
                  </div>
                </div>
              )}

              {/* Live Video Feed */}
              <video
                ref={webcamRef}
                autoPlay
                playsInline
                muted
                className={`camera-video ${facingMode === "user" ? "camera-video--mirrored" : ""}`}
                style={{
                  display: !imageSrc && cameraStatus === "ready" ? "block" : "none",
                }}
              />

              {/* Captured Image Preview */}
              {imageSrc && (
                <div className="captured-preview-container">
                  <img src={imageSrc} alt="Captured Selfie" className="captured-preview-img" />
                  <div className="captured-badge">
                    <FiCheckCircle className="badge-check-icon" />
                    <span>Photo Ready</span>
                  </div>
                </div>
              )}

              {/* Biometric Oval Guide Overlay */}
              {!imageSrc && cameraStatus === "ready" && (
                <div
                  className={`biometric-reticle ${alignmentState.isAligned ? "biometric-reticle--optimal" : ""
                    }`}
                >
                  <div className="biometric-oval" />
                  <div className="biometric-scan-laser" />
                  <div className="corner-bracket corner-bracket--tl" />
                  <div className="corner-bracket corner-bracket--tr" />
                  <div className="corner-bracket corner-bracket--bl" />
                  <div className="corner-bracket corner-bracket--br" />
                </div>
              )}
            </div>

            {/* Quality & Alignment Feedback Container (Moved Outside) */}
            {!imageSrc && cameraStatus === "ready" && (
              <div className="camera-feedback-container">
                <div className={`quality-banner quality-banner--${alignmentState.status}`}>
                  {alignmentState.status === "good" ? (
                    <FiCheckCircle className="quality-icon" />
                  ) : (
                    <FiInfo className="quality-icon" />
                  )}
                  <span>{alignmentState.message}</span>
                </div>

                <div
                  className={`alignment-lock-badge ${alignmentState.isAligned
                      ? "alignment-lock-badge--unlocked"
                      : "alignment-lock-badge--locked"
                    }`}
                >
                  {alignmentState.isAligned ? (
                    <>
                      <FiUnlock className="lock-icon" />
                      <span>Ready</span>
                    </>
                  ) : (
                    <>
                      <FiLock className="lock-icon" />
                      <span>Locked</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Hidden Analysis and File Pickers */}
            <canvas ref={canvasRef} style={{ display: "none" }} />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />

            {/* Bottom Actions Area */}
            <div className="selfie-actions-area">
              {!imageSrc ? (
                <div className="shutter-control-group">
                  <div className="shutter-btn-wrapper">
                    <button
                      type="button"
                      className={`shutter-outer-ring ${alignmentState.isAligned
                          ? "shutter-outer-ring--aligned"
                          : "shutter-outer-ring--disabled"
                        }`}
                      onClick={handleCaptureClick}
                      disabled={!alignmentState.isAligned || cameraStatus !== "ready" || isCapturing}
                      aria-label="Capture Selfie"
                    >
                      <div className="shutter-inner-btn">
                        {alignmentState.isAligned ? (
                          <FiCamera className="shutter-icon" />
                        ) : (
                          <FiLock className="shutter-icon shutter-icon--locked" />
                        )}
                      </div>
                    </button>
                  </div>

                  {/* Helper Text below Shutter Button */}
                  <div className="shutter-hint-text">
                    {alignmentState.isAligned ? (
                      <span className="hint-text--aligned">Click to capture your selfie</span>
                    ) : (
                      <span className="hint-text--locked">Face must be visible in the circle to take selfie</span>
                    )}
                  </div>

                  <button
                    type="button"
                    className="upload-fallback-link"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FiUploadCloud className="upload-link-icon" />
                    <span>Upload photo from device instead</span>
                  </button>
                </div>
              ) : (
                <div className="review-action-group">
                  <button
                    type="button"
                    className="btn-review-retake"
                    onClick={handleRetake}
                    disabled={isUploading}
                  >
                    <FiRotateCcw className="btn-icon" />
                    <span>Retake Photo</span>
                  </button>

                  <button
                    type="button"
                    className="btn-review-continue"
                    onClick={continueToNext}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <>
                        <div className="btn-spinner" />
                        <span>Validating Face...</span>
                      </>
                    ) : (
                      <>
                        <FiCheck className="btn-icon" />
                        <span>Save & Continue</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>


          {/* Compact Privacy Reassurance */}
          <div className="selfie-privacy-compact">
            <FiShield className="privacy-shield" />
            <span>Securely encrypted for your event only.</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SelfieCapture;